import { useEffect, useState } from 'react';
import { useApolloClient } from 'react-apollo';

import { getCheckoutPriceQuery } from '../../../../../../apps/temp/graphql/queries/getCheckoutPrice';
import { getPricedShippingOptions } from '../../helpers';

/**
 * Prices the given print partner shipping options for the current cart by
 * asking the platform for the checkout price with and without each option.
 *
 * @param {Object} opts The options.
 * @param {Object} opts.account The customer account.
 * @param {string} [opts.addressId] The customer shipping address id.
 * @param {Object} opts.checkoutItems The parsed checkout items.
 * @param {Array<Object>} opts.shippingOptions The print partner shipping options.
 * @returns {{pricedShippingOptions: Array<Object>, loading: boolean}}
 */
const useShippingOptionPrices = ({
  account,
  addressId,
  checkoutItems,
  shippingOptions,
}) => {
  const client = useApolloClient();
  // The priced options are keyed by the variables they were computed for, so
  // `loading` is true until the current variables have been priced.
  const [priced, setPriced] = useState({ key: null, shippingOptions: [] });
  const variablesKey = JSON.stringify({
    accountId: account?.id,
    addressId,
    checkoutItems,
    shippingOptionIds: (shippingOptions ?? []).map(({ id }) => id),
  });

  useEffect(() => {
    let cancelled = false;

    if (!account?.id || !shippingOptions?.length) {
      setPriced({ key: variablesKey, shippingOptions: [] });
      return undefined;
    }

    const fetchPrice = printPartnerShippingOptionId =>
      client
        .query({
          query: getCheckoutPriceQuery,
          variables: {
            account_id: account.id,
            address_id: addressId,
            print_partner_shipping_option_id: printPartnerShippingOptionId,
            ...checkoutItems,
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => data?.checkoutPrice ?? null)
        .catch(() => null);

    Promise.all([
      fetchPrice(undefined),
      ...shippingOptions.map(({ id }) => fetchPrice(id)),
    ]).then(([basePricing, ...optionPricings]) => {
      if (cancelled) {
        return;
      }

      setPriced({
        key: variablesKey,
        shippingOptions: getPricedShippingOptions(
          shippingOptions,
          basePricing,
          Object.fromEntries(
            shippingOptions.map(({ id }, index) => [id, optionPricings[index]]),
          ),
        ),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [variablesKey]);

  return {
    pricedShippingOptions: priced.shippingOptions,
    loading: priced.key !== variablesKey,
  };
};

export default useShippingOptionPrices;
